import { ApolloProvider, ApolloClient, ApolloLink } from '@apollo/client';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { CacheProvider } from '@emotion/react';
import { createUploadLink } from 'apollo-upload-client';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import App from './app';
import { DesignToken } from './components/base/core';
import { createEmotionCache } from './emotion-cache';
import { isMsalPopupResponse } from './utils/msal-utils';

import reduxStore from '~/client/redux';
import { GRAPHQL_API_PREFIX } from '~/constants';
import cache from '~/graphql/util/cache';

// Mute Apollo Client's internal canonizeResults deprecation (triggered by cache.diff; we don't use this option)
(globalThis as any)[Symbol.for('apollo.deprecations')] = true;

const centeredStyle =
  'display:flex;justify-content:center;align-items:center;height:100vh;font-family:system-ui';

const msalLoadingHtml = `<div style="${centeredStyle}">Completing authentication...</div>`;

const msalErrorHtml =
  `<div style="${centeredStyle};flex-direction:column;gap:8px">` +
  '<div>Authentication could not be completed.</div>' +
  '<div style="font-size:14px;color:' +
  DesignToken.textMuted +
  '">Please close this window and try again.</div>' +
  '</div>';

async function handleMsalPopupResponse(): Promise<void> {
  try {
    const bridgeModule = await import(
      /* webpackChunkName: "msal-redirect-bridge" */
      '@azure/msal-browser/redirect-bridge'
    );

    // Handle both ESM and CJS (webpack 4 compat)
    const broadcastFn =
      bridgeModule.broadcastResponseToMainFrame ??
      (bridgeModule as any).default?.broadcastResponseToMainFrame;

    if (typeof broadcastFn !== 'function') {
      throw new Error(
        'broadcastResponseToMainFrame not found in redirect bridge module'
      );
    }

    await broadcastFn();
  } catch (bridgeError) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[MSAL] Redirect bridge failed:', bridgeError);
    }

    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = msalErrorHtml;
    }
  }
}

const initApp = () => {
  const wasMsalPopup = isMsalPopupResponse();

  if (wasMsalPopup) {
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = msalLoadingHtml;
    }

    handleMsalPopupResponse();
    return;
  }

  if (process.env.NODE_ENV === 'development') {
    loadDevMessages();
    loadErrorMessages();
  }

  // Suppress noisy warnings from @nivo (defaultProps) and Apollo
  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'production'
  ) {
    const originalError = console.error;
    console.error = (...args: any[]) => {
      if (
        typeof args[0] === 'string' &&
        (args[0].includes('Support for defaultProps will be removed') ||
          args[0].includes('go.apollo.dev/c/err'))
      ) {
        return;
      }
      originalError.call(console, ...args);
    };
  }

  const store = reduxStore(window.__REDUX_STATE__);

  const httpLink = createUploadLink({
    uri: GRAPHQL_API_PREFIX,
    headers: {
      Accept: 'application/json',
      'Apollo-Require-Preflight': 'true',
      ...(window.__CSRF_TOKEN__ && { 'X-CSRF-Token': window.__CSRF_TOKEN__ }),
    },
  });

  const authLink = new ApolloLink((operation, forward) => {
    const currentOrgId = localStorage.getItem('currentOrgId');

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...(currentOrgId && { 'X-Org-Id': currentOrgId }),
      },
    }));

    return forward(operation);
  });

  const client = new ApolloClient({
    link: ApolloLink.from([authLink, httpLink as unknown as ApolloLink]),
    devtools: {
      enabled: process.env.NODE_ENV === 'development',
    },
    cache: cache.restore(window.__APOLLO_STATE__ as any),
  });

  (window as any).__APOLLO_CLIENT__ = client;

  // Create Emotion cache with same key used on server for hydration consistency
  const emotionCache = createEmotionCache();

  const root = hydrateRoot(
    document.getElementById('root')!,
    <CacheProvider value={emotionCache}>
      <ApolloProvider client={client}>
        <Provider store={store}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <App />
          </BrowserRouter>
        </Provider>
      </ApolloProvider>
    </CacheProvider>
  );

  if (module.hot) {
    module.hot.accept('./app', () => {
      root.render(
        <CacheProvider value={emotionCache}>
          <ApolloProvider client={client}>
            <Provider store={store}>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <App />
              </BrowserRouter>
            </Provider>
          </ApolloProvider>
        </CacheProvider>
      );
    });
  }
};

initApp();
